import React, { Component, createRef } from 'react';

// CSS
import './SiteLook.scss';

// Helper
import { checkCookie } from '../../Helper/Cookies';
import { apiFetch, platformInfo } from '../../Helper/Api';
import { usd, egp } from '../Billing/format';

// Components
import Header from '../../components/Header/Header';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Modal, Confirm, Panel } from '../../components/UI/Overlays';
import { Segmented } from '../../components/UI/Controls';

// Properties
const properties = require('../../properties.json');

const HEX6 = /^#[0-9a-fA-F]{6}$/;

// ---- colour maths: same as the student site (Helper/Theme.js there) and the server (Colors.java) ----
function rgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function luminance(hex) {
    return rgb(hex)
        .map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); })
        .reduce((s, v, i) => s + v * [0.2126, 0.7152, 0.0722][i], 0);
}
function contrast(a, b) {
    const x = luminance(a), y = luminance(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
function mix(a, b, t) {
    const x = rgb(a), y = rgb(b);
    return '#' + x.map((v, i) => ('0' + Math.round(v + (y[i] - v) * t).toString(16)).slice(-2)).join('');
}
function onColor(c) {
    return contrast(c, '#ffffff') >= contrast(c, '#111111') ? '#ffffff' : '#111111';
}

const FONT_WEIGHTS = {
    Cairo: '400;700', Tajawal: '400;700', Almarai: '400;700', 'IBM Plex Sans Arabic': '400;700', 'Readex Pro': '400;700',
    'Noto Kufi Arabic': '400;700', Changa: '400;700', 'El Messiri': '400;700', Amiri: '400;700', 'Reem Kufi': '400;700',
    'Baloo Bhaijaan 2': '400;700', Lalezar: null, Inter: '400;700', Poppins: '400;700', 'Baloo 2': '400;700',
    'DM Sans': '400;700', 'Playfair Display': '400;700',
};
function loadFont(name) {
    if (!name || !(name in FONT_WEIGHTS)) return;
    const id = 'font-' + name.replace(/\s+/g, '-');
    if (document.getElementById(id)) return;
    const w = FONT_WEIGHTS[name];
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=' + name.replace(/ /g, '+') + (w ? ':wght@' + w : '') + '&display=swap';
    document.head.appendChild(link);
}

function parse(def) {
    try { return JSON.parse(def); } catch (e) { return null; }
}

// "Site look": the teacher picks a theme for the student site (free, or bought once from the wallet), then its
// colours and fonts. Saved through PUT /teachers/me/theme; "preview on my site" opens the real site with the draft.
//
// Any theme can be looked at before it is used: the eye on a card opens the preview window -- its screenshot in a
// browser frame beside a live sample drawn from its own definition, recoloured by whichever of its presets is
// clicked. Nothing in the preview is saved; "اختيار" in it does exactly what the card's own button does.
/** The student site's address with a query string ready to extend. */
function siteBase(site) {
    if (window.location.hostname === 'localhost') return `http://localhost:3200/?host=${encodeURIComponent(site)}&`;
    return `https://${site}/?`;
}

/** The teacher's real site, scaled into the panel. It is loaded once per theme; every colour or font change after
 *  that is posted into it ({ type: "manasa:theme-draft" }, see Helper/Theme.js in the public site) and applied
 *  there at once. A shimmer covers it until the site says it is listening, so its saved colours never flash. */
class SiteFrame extends Component {
    box = createRef();
    frame = createRef();
    state = { scale: 0.3, ready: false };

    componentDidMount() {
        this.fit();
        window.addEventListener('resize', this.fit);
        window.addEventListener('message', this.onMessage);
    }

    componentDidUpdate(prev) {
        if (prev.device !== this.props.device) this.fit();
        if (prev.src !== this.props.src) this.setState({ ready: false });
        if (prev.draftKey !== this.props.draftKey) this.post();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.fit);
        window.removeEventListener('message', this.onMessage);
    }

    size = () => (this.props.device === 'mobile' ? { w: 390, h: 780 } : { w: 1280, h: 800 });

    fit = () => {
        const box = this.box.current;
        if (box) this.setState({ scale: box.clientWidth / this.size().w });
    };

    onMessage = (e) => {
        const win = this.frame.current && this.frame.current.contentWindow;
        if (!win || e.source !== win || !e.data || e.data.type !== 'manasa:theme-ready') return;
        this.post();
        this.setState({ ready: true });
    };

    post = () => {
        const win = this.frame.current && this.frame.current.contentWindow;
        if (win) win.postMessage({ type: 'manasa:theme-draft', choice: this.props.choice }, '*');
    };

    render() {
        const { w, h } = this.size();
        const { scale, ready } = this.state;
        return (
            <div className={`sl-frame ${ready ? 'is-ready' : ''}`} ref={this.box} style={{ height: Math.round(h * scale) }}>
                <iframe ref={this.frame} key={this.props.src} src={this.props.src} title="معاينة موقعك"
                    style={{ width: w, height: h, transform: `scale(${scale})` }} />
                <span className="sl-frame-wait" aria-hidden="true"><i className="pi pi-spin pi-spinner"></i>بنحمّل موقعك…</span>
            </div>
        );
    }
}

/** What a theme is made for (its "specialty"), in the teacher's words; "general" fits any subject. */
const SPECIALTY_NAMES = {
    general: 'عام', math: 'رياضيات', physics: 'فيزياء', chemistry: 'كيمياء', biology: 'أحياء', medicine: 'طب وتمريض',
    engineering: 'هندسة', arabic: 'لغة عربية', english: 'إنجليزي', languages: 'لغات أجنبية', programming: 'برمجة',
    design: 'تصميم وفنون', music: 'موسيقى', quran: 'قرآن وعلوم شرعية', history: 'تاريخ وجغرافيا', business: 'محاسبة وأعمال',
    skills: 'مهارات', fitness: 'جيم ولياقة', wellness: 'تغذية وصحة', kids: 'أطفال',
};
const specialtyOf = (t) => (parse(t.definition) || {}).specialty || null;

const FILTERS = [
    { key: 'all', label: 'الكل', icon: 'pi pi-th-large', test: () => true },
    { key: 'free', label: 'مجانية', icon: 'pi pi-gift', test: (t) => !t.paid },
    { key: 'paid', label: 'مدفوعة', icon: 'pi pi-star', test: (t) => t.paid && !t.owned },
    { key: 'mine', label: 'بتاعتي', icon: 'pi pi-check-circle', test: (t) => t.owned },
];

const LAYOUT_NAMES = {
    header: { bar: 'شريط ثابت', floating: 'عائم', glass: 'زجاجي شفاف', classic: 'كلاسيكي', minimal: 'بسيط بدون حدود' },
    hero: { split: 'نص وصورة جنب بعض', blob: 'أشكال دائرية', centered: 'في النص', arch: 'صورة على شكل قوس', text: 'نص بس', bold: 'عناوين عريضة', bento: 'صناديق' },
    courseCards: { lift: 'كروت مرفوعة', playful: 'مرحة وملونة', glow: 'متوهجة', framed: 'بإطار رفيع', rows: 'صفوف', tiles: 'بلاطات' },
    sectionTitles: { plain: 'عادية', 'accent-bar': 'بشريط ملون', dots: 'بنقط', ornament: 'بزخرفة', gradient: 'متدرجة', underline: 'بخط تحتها' },
    footer: { simple: 'بسيط', solid: 'ممتلئ بلون' },
    // the newer themes: the page's design language and the section of the teacher's specialty
    style: { aurora: 'ضباب ألوان', bento: 'صناديق منظّمة', brutal: 'جريء بحدود عريضة', glass: 'زجاج مضبّب', clay: 'ثلاثي الأبعاد ناعم',
        editorial: 'مجلة', swiss: 'شبكة سويسرية', paper: 'ورق كشكول', neon: 'نيون', mono: 'أبيض وأسود' },
    subject: { problem: 'مسألة الأسبوع', sim: 'تجربة بمحاكاة', periodic: 'جدول دوري', body: 'أجهزة الجسم', mcq: 'بنك أسئلة',
        tools: 'برامج هندسية', verse: 'بيت الأسبوع', levels: 'مستويات اللغة', clubs: 'نوادي محادثة', projects: 'مشاريع برمجة',
        gallery: 'معرض أعمال', tracks: 'تراكات تدريب', juz: 'متابعة الحفظ', timeline: 'خط زمني', cases: 'حالات عملية',
        roadmap: 'خريطة مهنية', plans: 'خطط اشتراك', meals: 'يوم صحي', journey: 'رحلة نجوم', features: 'مميزات المنصة',
        dashboard: 'لوحة الطالب', certificate: 'شهادة إتمام', calendar: 'تقويم الأسبوع', compare: 'قبل وبعد' },
};
const LAYOUT_PARTS = [
    ['header', 'الهيدر', 'pi pi-window-maximize'],
    ['hero', 'أول الصفحة', 'pi pi-image'],
    ['courseCards', 'كروت الكورسات', 'pi pi-th-large'],
    ['sectionTitles', 'عناوين الأقسام', 'pi pi-align-right'],
    ['footer', 'الفوتر', 'pi pi-minus'],
    ['style', 'أسلوب التصميم', 'pi pi-palette'],
    ['subject', 'قسم التخصص', 'pi pi-star'],
];
const SHADOW_NAMES = { none: 'بدون ظل', soft: 'ظل ناعم', strong: 'ظل واضح', hard: 'ظل حاد' };

class SiteLook extends Component {
    constructor(props) {
        super(props);
        this.toast = createRef(null);
    }

    state = {
        loading: true,
        catalog: null,
        site: null,
        // the draft being edited
        theme: null, primaryColor: '', accentColor: '', bgColor: '', fontBody: '', fontHeading: '',
        saving: false,
        buying: null,
        // display only
        filter: 'all',
        specialty: 'all',
        device: 'desktop',
        previewId: null,
        previewPreset: 0,
        confirmBuy: null,
        editOpen: false,
        stage: 'site', // the panel shows the real site when there is one, else the miniature
        // choosing the site's address right here (the same PUT /teachers/me/subdomain as onboarding's last step)
        baseDomain: null,
        linkOpen: false, linkValue: '', linkAvailable: null, linkChecking: false, linkSaving: false,
        applyAsk: null, // 'pick': a card was chosen and is asked about
    }

    componentDidMount() {
        document.title = properties.site_name + ' | شكل الموقع';
        if (!checkCookie('name')) {
            window.localStorage.removeItem('currentTeacher');
            window.location.href = `/`;
            return;
        }
        Promise.all([
            apiFetch('/teachers/me/themes'),
            apiFetch('/teachers/me').catch(() => null),
            platformInfo().catch(() => ({})),
        ]).then(([catalog, me, info]) => {
            let site = null;
            if (me && me.customDomain && me.customDomainStatus === 'VERIFIED') site = me.customDomain;
            else if (me && me.subdomain && info && info.baseDomain) site = `${me.subdomain}.${info.baseDomain}`;
            this.setState({ catalog, site, baseDomain: (info && info.baseDomain) || null, loading: false });
            this.takeSelection(catalog.selection);
        }).catch(error => {
            this.setState({ loading: false });
            this.toast.current.show({ severity: 'error', summary: 'لم يتم التحميل', detail: error.message || 'خطأ في الإتصال' });
        });
    }

    takeSelection = (s) => {
        this.setState({
            theme: s.theme,
            primaryColor: s.primaryColor || '', accentColor: s.accentColor || '', bgColor: s.bgColor || '',
            fontBody: s.fontBody || '', fontHeading: s.fontHeading || '',
        });
    }

    themeById = (id) => (this.state.catalog ? this.state.catalog.themes.find(t => t.id === id) : null);

    def = () => {
        const t = this.themeById(this.state.theme);
        return t ? parse(t.definition) : null;
    }

    // the colours actually in use: the teacher's own, else the theme's first preset
    colors = () => {
        const d = this.def();
        const p = (d && d.presets && d.presets[0]) || { primary: '#1e3a8a', accent: '#f59e0b', background: '#f6f8fc' };
        const ok = (v, f) => (HEX6.test(v) ? v : f);
        return { primary: ok(this.state.primaryColor, p.primary), accent: ok(this.state.accentColor, p.accent), bg: ok(this.state.bgColor, p.background) };
    }

    fonts = () => {
        const d = this.def();
        const body = this.state.fontBody || (d && d.fonts && d.fonts.body) || 'Cairo';
        const head = this.state.fontHeading || (d && d.fonts && d.fonts.heading) || body;
        return { body, head };
    }

    pickTheme = (t) => {
        if (!t.owned) return;
        // a new theme starts from its own colours and fonts
        this.setState({ theme: t.id, primaryColor: '', accentColor: '', bgColor: '', fontBody: '', fontHeading: '' });
    }

    buy = (t) => {
        const c = this.state.catalog;
        if (c && Number(c.walletUsd) < Number(t.priceUsd)) {
            this.toast.current.show({ severity: 'warn', summary: 'الرصيد مش كفاية', detail: `رصيدك ${usd(c.walletUsd)} — اشحن المحفظة من صفحة "اشتراكي" الأول` });
            return;
        }
        // asked on the Confirm card; the purchase itself is confirmBuyNow
        this.setState({ confirmBuy: t });
    }

    confirmBuyNow = () => {
        const t = this.state.confirmBuy;
        if (!t) return;
        this.setState({ buying: t.id });
        apiFetch(`/teachers/me/themes/${t.id}/purchase`, { method: 'POST' })
            .then(catalog => {
                this.setState({ catalog, buying: null, confirmBuy: null, theme: t.id, primaryColor: '', accentColor: '', bgColor: '', fontBody: '', fontHeading: '' });
                this.toast.current.show({ severity: 'success', summary: 'تم الشراء', detail: `الثيم "${t.nameAr}" بقى بتاعك — اختار ألوانه واضغط حفظ` });
            })
            .catch(error => {
                this.setState({ buying: null, confirmBuy: null });
                this.toast.current.show({ severity: 'error', summary: 'لم يتم الشراء', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    previewUrl = (themeId, withDraft) => {
        const { site } = this.state;
        if (!site) return null;
        const q = new URLSearchParams({ themePreview: themeId });
        if (withDraft) {
            const c = this.colors();
            q.set('c', [c.primary, c.accent, c.bg].join(','));
            if (this.state.fontBody) q.set('fb', this.state.fontBody);
            if (this.state.fontHeading) q.set('fh', this.state.fontHeading);
        }
        return `${siteBase(site)}${q.toString()}`;
    }

    save = () => {
        const s = this.state;
        const c = this.colors();
        if (contrast(c.primary, c.bg) < 3) {
            this.toast.current.show({ severity: 'error', summary: 'الألوان مش واضحة', detail: 'اللون الأساسي مش باين على الخلفية — اختار لون أغمق أو خلفية أفتح' });
            return;
        }
        this.setState({ saving: true });
        apiFetch('/teachers/me/theme', {
            method: 'PUT',
            body: {
                theme: s.theme,
                primaryColor: s.primaryColor || null, accentColor: s.accentColor || null, bgColor: s.bgColor || null,
                fontBody: s.fontBody || null, fontHeading: s.fontHeading || null,
            },
        }).then(catalog => {
            this.setState({ catalog, saving: false, editOpen: false, applyAsk: null });
            this.takeSelection(catalog.selection);
            this.toast.current.show({ severity: 'success', summary: 'تم الحفظ', detail: 'موقعك اتغيّر للشكل الجديد' });
        }).catch(error => {
            this.setState({ saving: false });
            this.toast.current.show({ severity: 'error', summary: 'لم يتم الحفظ', detail: error.message || 'خطأ في الإتصال' });
        });
    }

    // ---- the preview window ----

    shown = () => {
        const f = FILTERS.find(x => x.key === this.state.filter) || FILTERS[0];
        const sp = this.state.specialty;
        return this.state.catalog ? this.state.catalog.themes.filter(f.test).filter(t => sp === 'all' || specialtyOf(t) === sp) : [];
    }

    /** Which of a theme's colour sets these colours are (the first set stands for "no colours of its own"),
     *  or -1 when they are custom. */
    presetIndexOf = (t, primary, accent, bg) => {
        const presets = ((t && parse(t.definition)) || {}).presets || [];
        if (!primary && !accent && !bg) return presets.length ? 0 : -1;
        return presets.findIndex((p) => p.primary === primary && p.accent === accent && p.background === bg);
    }

    // opens on the colours the teacher has now for this theme -- the draft, else what is saved -- not the first set
    openPreview = (t) => {
        const s = this.state;
        const sel = s.catalog.selection;
        let index = 0;
        if (t.id === s.theme) index = this.presetIndexOf(t, s.primaryColor, s.accentColor, s.bgColor);
        else if (t.id === sel.theme) index = this.presetIndexOf(t, sel.primaryColor, sel.accentColor, sel.bgColor);
        this.setState({ previewId: t.id, previewPreset: Math.max(index, 0) });
    }

    closePreview = () => this.setState({ previewId: null });

    // "اختيار" from the preview takes the theme with the colours shown in it
    // and always says what happened: the apply window when that differs from what is saved -- the saved theme in
    // another of its colour sets included -- or a note when it is exactly what the site already shows.
    pickFromPreview = (t, preset, index) => {
        if (!t.owned) return;
        const saved = this.state.catalog.selection;
        const sameTheme = t.id === saved.theme;
        const next = {
            theme: t.id,
            // the first set is the theme's own default, saved as "no colours of its own"
            primaryColor: index > 0 && preset ? preset.primary : '',
            accentColor: index > 0 && preset ? preset.accent : '',
            bgColor: index > 0 && preset ? preset.background : '',
            // staying on the saved theme keeps the saved fonts; a new theme starts from its own
            fontBody: sameTheme ? saved.fontBody || '' : '',
            fontHeading: sameTheme ? saved.fontHeading || '' : '',
        };
        const unchanged = ['theme', 'primaryColor', 'accentColor', 'bgColor', 'fontBody', 'fontHeading']
            .every((k) => (next[k] || null) === (saved[k] || null));
        this.setState({ ...next, previewId: null, applyAsk: unchanged ? null : 'pick' });
        if (unchanged) {
            this.toast.current.show({ severity: 'info', summary: 'ده شكل موقعك الحالي', detail: `ثيم «${t.nameAr}» بالألوان دي هو اللي على موقعك دلوقتي` });
        }
    }

    // ---- the apply window ----
    // A theme chosen on a card is asked about at once (apply it, tune it first, or leave it). Closing the panel
    // without saving drops the edits: the saved look comes back.

    /** A card's "choose": the saved theme brings back what is saved, any other one asks to apply it. */
    choose = (t) => {
        if (!t.owned) return;
        const saved = this.state.catalog.selection;
        if (t.id === saved.theme) {
            this.takeSelection(saved);
            return;
        }
        this.pickTheme(t);
        this.setState({ applyAsk: 'pick' });
    }

    closeEditor = () => {
        this.takeSelection(this.state.catalog.selection);
        this.setState({ editOpen: false });
    }

    discard = () => {
        this.takeSelection(this.state.catalog.selection);
        this.setState({ applyAsk: null });
    }

    tuneFirst = () => this.setState({ applyAsk: null, editOpen: true });

    // ---- pieces ----

    tagOf = (t) => {
        if (!t.paid) return <span className="sl-tag free">مجاني</span>;
        if (t.owned) return <span className="sl-tag owned"><i className="pi pi-star"></i>مملوك</span>;
        return <span className="sl-tag paid">{usd(t.priceUsd)}</span>;
    }

    /** The student site in miniature, drawn from a theme definition with the given colours and fonts. It is laid out
     *  by the theme's own blocks -- header, hero, course cards, section titles, footer -- with the same names the site
     *  uses (docs/themes/THEME-SPEC.md), so picking a theme here shows its shape and not only its colours. The colour
     *  maths is the site's own (Helper/Theme.js there). */
    sample = (d, c, f) => {
        const rtl = !d || d.direction !== 'ltr';
        const dark = luminance(c.bg) < 0.18;
        let ink = dark ? '#eef0f5' : mix(c.primary, '#0b0d12', 0.82);
        if (contrast(ink, c.bg) < 7) ink = dark ? '#ffffff' : '#0b0d12';
        const shape = (d && d.shape) || {};
        const layout = (d && d.layout) || {};
        const num = (v, fallback) => (v != null ? v : fallback);
        const shadows = {
            none: ['none', 'none'],
            soft: ['0 1px 4px rgba(15,23,42,.08)', '0 10px 22px -10px rgba(15,23,42,.28)'],
            strong: ['0 4px 10px -4px rgba(0,0,0,.25)', '0 14px 28px -10px rgba(0,0,0,.4)'],
        };
        const sh = shadows[shape.shadow] || shadows.soft;
        const vars = {
            '--m-p': c.primary,
            '--m-a': c.accent,
            '--m-bg': c.bg,
            '--m-ink': ink,
            '--m-muted': mix(ink, c.bg, 0.42),
            '--m-line': mix(ink, c.bg, dark ? 0.82 : 0.86),
            '--m-surface': dark ? mix(c.bg, '#ffffff', 0.06) : mix(c.bg, '#ffffff', 0.75),
            '--m-p-soft': mix(c.primary, c.bg, dark ? 0.8 : 0.88),
            '--m-a-soft': mix(c.accent, c.bg, dark ? 0.8 : 0.82),
            '--m-on-p': onColor(c.primary),
            '--m-on-a': onColor(c.accent),
            // the site's radii, scaled down with the rest of the miniature
            '--m-r': `${Math.round(Math.min(num(shape.radius, 14), 40) * 0.55)}px`,
            '--m-rl': `${Math.round(Math.min(num(shape.radiusLarge, 22), 60) * 0.55)}px`,
            '--m-rb': `${Math.min(num(shape.buttonRadius, 10), 999)}px`,
            '--m-bw': `${Math.min(num(shape.borderWidth, 1), 2)}px`,
            '--m-sh-sm': sh[0],
            '--m-sh': sh[1],
            '--m-fb': `'${f.body}'`,
            '--m-fh': `'${f.head}'`,
        };
        const L = rtl
            ? { name: 'اسم المدرس', login: 'دخول', eyebrow: 'التسجيل مفتوح', title: 'افهم الدرس صح', text: 'شرح مبسّط ومسائل محلولة خطوة بخطوة.', go: 'تصفّح الكورسات', join: 'إنشاء حساب', sec: 'الكورسات', cards: ['الترم الأول', 'المراجعة النهائية', 'بنك الأسئلة'], label: 'منصة تعليمية', foot: 'كل الحقوق محفوظة' }
            : { name: 'Teacher name', login: 'Login', eyebrow: 'Enrolment open', title: 'Learn it the right way', text: 'Clear lessons and solved problems, step by step.', go: 'Explore courses', join: 'Join', sec: 'Courses', cards: ['First term', 'Final review', 'Question bank'], label: 'Learning platform', foot: 'All rights reserved' };

        return (
            <div className="sl-mini" dir={rtl ? 'rtl' : 'ltr'} style={vars}
                data-header={layout.header || 'bar'} data-hero={layout.hero || 'split'} data-cards={layout.courseCards || 'lift'}
                data-titles={layout.sectionTitles || 'plain'} data-footer={layout.footer || 'simple'} data-style={layout.style || 'none'}>
                <div className="sl-mini-bar">
                    <b className="sl-mini-brand"><i></i>{L.name}</b>
                    <span className="sl-mini-nav" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
                    <span className="sl-mini-login">{L.login}</span>
                </div>

                <div className="sl-mini-hero">
                    <div className="sl-mini-copy">
                        <span className="sl-mini-eyebrow">{L.eyebrow}</span>
                        <b className="sl-mini-title">{L.title}</b>
                        <span className="sl-mini-text">{L.text}</span>
                        <span className="sl-mini-actions">
                            <span className="sl-mini-btn">{L.go}</span>
                            <span className="sl-mini-btn is-ghost">{L.join}</span>
                        </span>
                    </div>
                    <div className="sl-mini-visual"><span>{L.label}</span></div>
                    <span className="sl-mini-boxes" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
                </div>

                <div className="sl-mini-sec">
                    <b className="sl-mini-h">{L.sec}</b>
                    <div className="sl-mini-cards">
                        {L.cards.map((name) => (
                            <span className="sl-mini-card" key={name}>
                                <i></i>
                                <b>{name}</b>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="sl-mini-foot">
                    <b>{L.name}</b>
                    <span>© {L.foot}</span>
                </div>
            </div>
        );
    }

    browser = (children, extraClass) => (
        <div className={`sl-browser ${extraClass || ''}`}>
            <div className="sl-browser-bar" aria-hidden="true">
                <span className="sl-dots"><i></i><i></i><i></i></span>
                <span className="sl-url" dir="ltr"><i className="pi pi-lock"></i>{this.state.site || 'your-site.manasa'}</span>
            </div>
            <div className="sl-browser-view">{children}</div>
        </div>
    )

    themeCard = (t, index) => {
        const current = this.state.catalog.selection.theme === t.id;
        const picked = this.state.theme === t.id;
        const d = parse(t.definition) || {};
        const preview = t.previewUrl || `/theme-previews/${t.id}.jpg`;
        const presets = (d.presets || []).slice(0, 5);
        return (
            <div key={t.id} className={`sl-theme ${picked ? 'picked' : ''} ${t.owned ? '' : 'locked'}`} style={{ '--i': index }}>
                <button type="button" className="sl-shot" onClick={() => (t.owned ? this.choose(t) : this.openPreview(t))} aria-label={t.nameAr}>
                    <img src={preview} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                </button>
                <div className="sl-shot-tags">
                    {current && <span className="sl-badge current"><i className="pi pi-circle-on"></i>الحالي</span>}
                    {this.tagOf(t)}
                </div>

                <div className="sl-glass">
                    <div className="sl-glass-row">
                        <b>{t.nameAr}</b>
                        {picked && <span className="sl-pick-mark"><i className="pi pi-check"></i>مختار</span>}
                        <span className="sl-swatches">
                            {presets.map((p, i) => (
                                <span key={i} title={p.name}><i style={{ background: p.primary }}></i><i style={{ background: p.accent }}></i></span>
                            ))}
                        </span>
                    </div>
                    <span className="sl-glass-hint">عدّي عليه عشان تشوف التفاصيل</span>
                    <div className="sl-more">
                        <div>
                            {t.description && <p>{t.description}</p>}
                            <div className="sl-theme-actions">
                                {t.owned
                                    ? (
                                        <button type="button" className={`ui-btn is-small is-secondary ${picked ? 'is-on' : ''}`} onClick={() => this.choose(t)}>
                                            {picked && <i className="pi pi-check"></i>}
                                            {picked ? 'مختار' : 'اختيار'}
                                        </button>
                                    )
                                    : (
                                        <button type="button" className="ui-btn is-small" disabled={!!this.state.buying} onClick={() => this.buy(t)}>
                                            <i className="pi pi-shopping-cart"></i>
                                            {this.state.buying === t.id ? 'جاري الشراء...' : `شراء ${usd(t.priceUsd)}`}
                                        </button>
                                    )}
                                <button type="button" className="ui-btn is-small is-ghost sl-eye" onClick={() => this.openPreview(t)}>
                                    <i className="pi pi-eye"></i>
                                    معاينة
                                </button>
                                {picked && (
                                    <button type="button" className="ui-btn is-small is-ghost sl-tune" title="تخصيص الألوان والخطوط" aria-label="تخصيص الألوان والخطوط"
                                        onClick={() => this.setState({ editOpen: true })}>
                                        <i className="pi pi-sliders-h"></i>
                                    </button>
                                )}
                            </div>
                            {!t.owned && <small className="sl-note">≈ {egp(t.priceEgp)} — بتدفعه مرة واحدة من المحفظة</small>}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ---- the site's address, chosen here when the teacher has none yet ----
    // Same rules and calls as onboarding's last step: lower-case letters, digits and "-", at least 3, checked as typed.
    onLinkChange = (value) => {
        const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
        this.setState({ linkValue: clean, linkAvailable: null });
        clearTimeout(this.linkTimer);
        if (clean.length < 3) return;
        this.linkTimer = setTimeout(() => {
            this.setState({ linkChecking: true });
            apiFetch('/teachers/subdomain-available', { query: { value: clean } })
                .then((res) => this.state.linkValue === clean && this.setState({ linkAvailable: res.available, linkChecking: false }))
                .catch(() => this.setState({ linkChecking: false }));
        }, 400);
    }

    saveLink = () => {
        const { linkValue, baseDomain } = this.state;
        if (linkValue.length < 3) return;
        this.setState({ linkSaving: true });
        apiFetch('/teachers/me/subdomain', { method: 'PUT', body: { subdomain: linkValue } })
            .then((me) => {
                const sub = (me && me.subdomain) || linkValue;
                this.setState({ linkSaving: false, linkOpen: false, site: baseDomain ? `${sub}.${baseDomain}` : null });
                this.toast.current.show({ severity: 'success', summary: 'موقعك جاهز', detail: 'دوس «افتح موقعي» وشوف الشكل اللي اخترته' });
            })
            .catch((error) => {
                this.setState({ linkSaving: false });
                this.toast.current.show({ severity: 'error', summary: 'تعذر الحفظ', detail: error.message || 'خطأ في الإتصال' });
            });
    }

    linkWindow = () => {
        const { linkOpen, linkValue, linkAvailable, linkChecking, linkSaving, baseDomain } = this.state;
        const ok = linkValue.length >= 3 && linkAvailable === true;
        let state = null;
        if (linkValue.length > 0 && linkValue.length < 3) state = <span className="sl-link-state">3 حروف على الأقل</span>;
        else if (linkChecking) state = <span className="sl-link-state"><i className="pi pi-spin pi-spinner"></i>بنشوف الاسم متاح ولا لأ…</span>;
        else if (linkAvailable === true) state = <span className="sl-link-state is-ok"><i className="pi pi-check-circle"></i>متاح</span>;
        else if (linkAvailable === false) state = <span className="sl-link-state is-bad"><i className="pi pi-times-circle"></i>الاسم ده محجوز، جرّب اسم تاني</span>;

        return (
            <Modal visible={linkOpen} onHide={() => !linkSaving && this.setState({ linkOpen: false })} width={480}
                title="اختار رابط موقعك"
                footer={
                    <button type="button" className="ui-btn is-gold" disabled={!ok || linkSaving} onClick={this.saveLink}>
                        <i className={linkSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}></i>
                        {linkSaving ? 'جاري الحفظ...' : 'حفظ الرابط'}
                    </button>
                }>
                <div className="sl-link">
                    <p>ده العنوان اللي طلابك هيدخلوا منه على موقعك، وعليه هتشوف الثيم والألوان اللي بتختارها هنا.</p>
                    <label htmlFor="site-link">اسم الموقع</label>
                    <div className={`sl-link-field ${linkAvailable === false ? 'is-bad' : ''} ${ok ? 'is-ok' : ''}`} dir="ltr">
                        <input id="site-link" type="text" autoFocus value={linkValue} placeholder="my-site" maxLength={40}
                            onChange={(e) => this.onLinkChange(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && ok && this.saveLink()} />
                        <span>.{baseDomain || 'manasa.igma-it.com'}</span>
                    </div>
                    <div className="sl-link-foot">{state}</div>
                </div>
            </Modal>
        );
    }

    /** "Apply it?" -- the site as it is now, the arrow, the site as it would be. */
    applyWindow = (theme, d, c, changed, ratio) => {
        const ask = this.state.applyAsk;
        const catalog = this.state.catalog;
        if (!catalog) return null;
        const sel = catalog.selection;
        const before = this.themeById(sel.theme);
        const visible = !!(ask && theme && d && changed);
        const shotOf = (t) => (t ? (t.previewUrl || `/theme-previews/${t.id}.jpg`) : '');
        const savedColors = (() => {
            const bd = before && parse(before.definition);
            const p = (bd && bd.presets && bd.presets[0]) || {};
            return [sel.primaryColor || p.primary, sel.accentColor || p.accent, sel.bgColor || p.background].filter(Boolean);
        })();

        return (
            <Dialog visible={visible} onHide={this.discard} showHeader={false}
                dismissableMask draggable={false} className="sl-apply-dialog" style={{ width: 'min(620px, 94vw)' }}>
                {visible && (
                    <div className="sl-apply" key={ask + theme.id}>
                        <button type="button" className="sl-apply-x" aria-label="إغلاق" onClick={this.discard}>
                            <i className="pi pi-times"></i>
                        </button>

                        <div className="sl-apply-flow" aria-hidden="true">
                            <figure className="is-from">
                                <img src={shotOf(before)} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                <span className="sl-apply-chips">{savedColors.map((x, i) => <i key={i} style={{ background: x }}></i>)}</span>
                                <figcaption>دلوقتي{before ? ` · ${before.nameAr}` : ''}</figcaption>
                            </figure>
                            <svg className="sl-apply-arrow" viewBox="0 0 64 24" fill="none">
                                <path d="M60 12H6m0 0 8-8M6 12l8 8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <figure className="is-to">
                                <img src={shotOf(theme)} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                                <span className="sl-apply-chips">{[c.primary, c.accent, c.bg].map((x, i) => <i key={i} style={{ background: x }}></i>)}</span>
                                <figcaption>الجديد · {theme.nameAr}</figcaption>
                            </figure>
                        </div>

                        <div className="sl-apply-text">
                            <b>تطبّق ثيم «{theme.nameAr}» على موقعك؟</b>
                            <p>طلابك هيشوفوا الشكل الجديد أول ما تحفظ. تقدر تظبط الألوان والخطوط الأول لو حابب.</p>
                            {ratio < 3 && <div className="sl-warn"><i className="pi pi-exclamation-triangle"></i>اللون الأساسي مش واضح على الخلفية — ظبّط الألوان الأول.</div>}
                        </div>

                        <div className="sl-apply-actions">
                            <button type="button" className="ui-btn is-gold" disabled={this.state.saving || ratio < 3} onClick={this.save}>
                                <i className={this.state.saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}></i>
                                {this.state.saving ? 'جاري الحفظ...' : 'حفظ وتطبيق'}
                            </button>
                            <button type="button" className="ui-btn is-secondary" onClick={this.tuneFirst}>
                                <i className="pi pi-sliders-h"></i>
                                تخصيص الألوان الأول
                            </button>
                            <button type="button" className="ui-btn is-ghost" onClick={this.discard}>
                                إلغاء
                            </button>
                        </div>
                    </div>
                )}
            </Dialog>
        );
    }

    previewWindow = () => {
        const t = this.themeById(this.state.previewId);
        const d = (t && parse(t.definition)) || {};
        const presets = d.presets || [];
        const index = Math.min(this.state.previewPreset, Math.max(presets.length - 1, 0));
        const p = presets[index] || { primary: '#1e3a8a', accent: '#f59e0b', background: '#f6f8fc' };
        // the set that is on the teacher's site right now, marked in the list
        const sel = this.state.catalog ? this.state.catalog.selection : {};
        const savedIndex = t && t.id === sel.theme ? this.presetIndexOf(t, sel.primaryColor, sel.accentColor, sel.bgColor) : -1;
        const f = { body: (d.fonts && d.fonts.body) || 'Cairo', head: (d.fonts && (d.fonts.heading || d.fonts.body)) || 'Cairo' };
        if (t) { loadFont(f.body); loadFont(f.head); }
        const picked = t && this.state.theme === t.id;
        const shape = d.shape || {};
        const layout = d.layout || {};
        const rtl = d.direction !== 'ltr';

        let siteLink = null;
        if (t && this.state.site) {
            const q = new URLSearchParams({ themePreview: t.id });
            if (index > 0) q.set('c', [p.primary, p.accent, p.background].join(','));
            siteLink = `${siteBase(this.state.site)}${q.toString()}`;
        }

        const footer = t && (
            <div className="sl-pv-foot">
                <span className="sl-pv-price">
                    {!t.paid ? 'مجاني لكل المدرسين' : t.owned ? 'الثيم ده بتاعك' : <>بتدفعه مرة واحدة: <b dir="ltr">{usd(t.priceUsd)}</b> ≈ {egp(t.priceEgp)}</>}
                </span>
                {siteLink && (
                    <a className="ui-btn is-small is-ghost" href={siteLink} target="_blank" rel="noopener noreferrer">
                        <i className="pi pi-external-link"></i>
                        فتح على موقعي
                    </a>
                )}
                {t.owned
                    ? (
                        <button type="button" className="ui-btn is-small" onClick={() => this.pickFromPreview(t, p, index)}>
                            <i className="pi pi-check"></i>
                            {picked ? 'اختيار الألوان دي' : 'اختيار الثيم ده'}
                        </button>
                    )
                    : (
                        <button type="button" className="ui-btn is-small" disabled={!!this.state.buying} onClick={() => { this.closePreview(); this.buy(t); }}>
                            <i className="pi pi-shopping-cart"></i>
                            {`شراء ${usd(t.priceUsd)}`}
                        </button>
                    )}
            </div>
        );

        const spec = (label, value, extra) => (
            <div className="sl-spec-row">
                <span>{label}</span>
                <b>{value}</b>
                {extra}
            </div>
        );

        return (
            <Modal visible={!!t} onHide={this.closePreview} width={1120} footer={footer}
                title={t ? <span className="sl-pv-title">ثيم «{t.nameAr}» <small dir="ltr">{t.nameEn}</small> {this.tagOf(t)}</span> : ''}>
                {t && (
                    <div className="sl-pv">
                        {t.description && <p className="sl-pv-desc">{t.description}</p>}

                        <div className="sl-pv-top">
                            <figure className="sl-pv-shot">
                                {this.browser(
                                    <img src={t.previewUrl || `/theme-previews/${t.id}.jpg`} alt={t.nameAr}
                                        onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />,
                                    'is-shot'
                                )}
                                <figcaption>الصفحة الرئيسية بالثيم ده</figcaption>
                            </figure>
                            <figure className="sl-pv-live">
                                <div className="sl-pv-sample" key={index}>
                                    {this.sample(d, { primary: p.primary, accent: p.accent, bg: p.background }, f)}
                                </div>
                                <figcaption>عيّنة حيّة بألوان «{(presets[index] && presets[index].name) || '—'}»</figcaption>
                            </figure>
                        </div>

                        <div className="sl-specs">
                            <section className="sl-spec is-wide">
                                <header><i className="pi pi-palette"></i>الألوان الجاهزة <small>({presets.length}) — دوس على أي واحدة تشوفها في العيّنة</small></header>
                                <div className="sl-spec-palettes">
                                    {presets.map((x, i) => (
                                        <button type="button" key={i} className={`sl-palette ${i === index ? 'on' : ''}`} onClick={() => this.setState({ previewPreset: i })}>
                                            <span className="sl-palette-strip">
                                                <i style={{ background: x.background }}></i>
                                                <i style={{ background: x.primary }}></i>
                                                <i style={{ background: x.accent }}></i>
                                            </span>
                                            <b>
                                                {x.name}{i === 0 && <small> (الأساسية)</small>}
                                                {i === savedIndex && <span className="sl-palette-now"><i className="pi pi-check"></i>على موقعك</span>}
                                            </b>
                                            <span className="sl-palette-hex" dir="ltr">{x.primary} · {x.accent} · {x.background}</span>
                                        </button>
                                    ))}
                                </div>
                            </section>

                            <section className="sl-spec">
                                <header><i className="pi pi-sort-alpha-down"></i>الخطوط</header>
                                <div className="sl-font-row">
                                    <span>خط العناوين</span>
                                    <b style={{ fontFamily: `'${f.head}', sans-serif` }}>{f.head}</b>
                                    <em style={{ fontFamily: `'${f.head}', sans-serif` }}>{rtl ? 'افهم الدرس صح' : 'Learn it right'}</em>
                                </div>
                                <div className="sl-font-row">
                                    <span>خط النص</span>
                                    <b style={{ fontFamily: `'${f.body}', sans-serif` }}>{f.body}</b>
                                    <em style={{ fontFamily: `'${f.body}', sans-serif` }}>{rtl ? 'شرح مبسّط ومسائل محلولة خطوة بخطوة' : 'Clear lessons, step by step'}</em>
                                </div>
                            </section>

                            <section className="sl-spec">
                                <header><i className="pi pi-table"></i>التخطيط</header>
                                {LAYOUT_PARTS.map(([key, label, icon]) => layout[key] && layout[key] !== 'none' && (
                                    <div className="sl-spec-row" key={key}>
                                        <span><i className={icon}></i>{label}</span>
                                        <b>{(LAYOUT_NAMES[key] && LAYOUT_NAMES[key][layout[key]]) || layout[key]}</b>
                                    </div>
                                ))}
                            </section>

                            <section className="sl-spec">
                                <header><i className="pi pi-clone"></i>الشكل</header>
                                {spec('استدارة الكروت', `${shape.radius != null ? shape.radius : 14}px`,
                                    <i className="sl-shape" style={{ borderRadius: Math.min(shape.radius != null ? shape.radius : 14, 14) }}></i>)}
                                {spec('استدارة الأقسام الكبيرة', `${shape.radiusLarge != null ? shape.radiusLarge : 22}px`,
                                    <i className="sl-shape is-wide" style={{ borderRadius: Math.min(shape.radiusLarge != null ? shape.radiusLarge : 22, 14) }}></i>)}
                                {spec('استدارة الأزرار', `${shape.buttonRadius != null ? shape.buttonRadius : 10}px`,
                                    <i className="sl-shape is-btn" style={{ borderRadius: Math.min(shape.buttonRadius != null ? shape.buttonRadius : 10, 999), background: p.primary }}></i>)}
                                {spec('سُمك الحدود', `${shape.borderWidth != null ? shape.borderWidth : 1}px`)}
                                {spec('الظل', SHADOW_NAMES[shape.shadow] || shape.shadow || 'ناعم',
                                    <i className={`sl-shape is-shadow-${shape.shadow || 'soft'}`}></i>)}
                            </section>

                            <section className="sl-spec">
                                <header><i className="pi pi-globe"></i>اللغة والاتجاه</header>
                                {spec('لغة الموقع', d.language === 'en' ? 'إنجليزي' : 'عربي')}
                                {spec('الاتجاه', rtl ? 'من اليمين للشمال' : 'من الشمال لليمين')}
                                {spec('السعر', !t.paid ? 'مجاني' : t.owned ? 'مملوك' : usd(t.priceUsd))}
                            </section>
                        </div>
                    </div>
                )}
            </Modal>
        );
    }

    colorInput = (key, label, fallback) => {
        const value = this.state[key];
        return (
            <label className="sl-color">
                <span>{label}</span>
                <span className="sl-color-row">
                    <span className="sl-color-chip" style={{ background: HEX6.test(value) ? value : fallback }}>
                        <input type="color" value={HEX6.test(value) ? value : fallback} onChange={(e) => this.setState({ [key]: e.target.value })} />
                    </span>
                    <input type="text" dir="ltr" value={value} placeholder={fallback} maxLength={7}
                        onChange={(e) => this.setState({ [key]: e.target.value.trim() })} />
                </span>
            </label>
        );
    }

    step = (n, title, hint, body) => (
        <section className="sl-step" style={{ '--i': n }}>
            <header>
                <span className="sl-step-n">{n}</span>
                <div>
                    <b>{title}</b>
                    <small>{hint}</small>
                </div>
            </header>
            {body}
        </section>
    )

    render() {
        const { loading, catalog, saving, site, filter, device } = this.state;
        const d = this.def();
        const c = this.colors();
        const f = this.fonts();
        loadFont(f.body);
        loadFont(f.head);
        const rtl = !d || d.direction !== 'ltr';
        const ratio = contrast(c.primary, c.bg);
        const theme = catalog && this.themeById(this.state.theme);
        const sel = (catalog && catalog.selection) || {};
        const changed = catalog && JSON.stringify({
            theme: sel.theme, fontBody: sel.fontBody || null, fontHeading: sel.fontHeading || null,
            primaryColor: sel.primaryColor || null, accentColor: sel.accentColor || null, bgColor: sel.bgColor || null,
        }) !== JSON.stringify({
            theme: this.state.theme, fontBody: this.state.fontBody || null, fontHeading: this.state.fontHeading || null,
            primaryColor: this.state.primaryColor || null, accentColor: this.state.accentColor || null, bgColor: this.state.bgColor || null,
        });
        const shown = this.shown();

        return (
            <>
                <Header pageName="شكل الموقع" />
                <Toast ref={this.toast}></Toast>
                <div className="content site-look">
                    <div className="container">
                        {loading && (
                            <div className="card sl-loading" aria-busy="true">
                                <div className="sl-grid">
                                    {[0, 1, 2].map(i => <div key={i} className="sl-skel"><i></i><b></b><b></b></div>)}
                                </div>
                            </div>
                        )}
                        {!loading && catalog && (
                            <>
                                <div className="card">
                                    <Card>
                                        <div className="sl-top">
                                            <div className="sl-top-text">
                                                <ul className="sl-lead">
                                                    <li><i className="pi pi-desktop"></i>الثيم بيغيّر شكل موقع الطلاب بس: الهيدر، الصفحة الرئيسية، كروت الكورسات والخطوط.</li>
                                                    <li><i className="pi pi-wallet"></i>الثيمات المدفوعة بتتدفع مرة واحدة من المحفظة.</li>
                                                </ul>
                                            </div>
                                            <div className="sl-top-meta">
                                                {/* the way to the site this page changes -- on localhost the student site on :3200
                                                    with this teacher's host, so the saved look is one click away */}
                                                {site ? (
                                                    <a className="sl-site-link" href={siteBase(site)} target="_blank" rel="noopener noreferrer">
                                                        <i className="pi pi-external-link"></i>
                                                        <span>
                                                            <b>افتح موقعي</b>
                                                            <small dir="ltr">{site}</small>
                                                        </span>
                                                    </a>
                                                ) : (
                                                    <button type="button" className="sl-site-link is-missing" onClick={() => this.setState({ linkOpen: true })}>
                                                        <i className="pi pi-link"></i>
                                                        <span>
                                                            <b>اختار رابط موقعك</b>
                                                            <small>علشان تشوف الشكل على موقعك</small>
                                                        </span>
                                                    </button>
                                                )}
                                                <span className="sl-wallet">
                                                    <i className="pi pi-wallet"></i>
                                                    <span>رصيد المحفظة</span>
                                                    <b dir="ltr">{usd(catalog.walletUsd)}</b>
                                                </span>
                                                {theme && d && (
                                                    <button type="button" className="ui-btn sl-open-edit" onClick={() => this.setState({ editOpen: true })}>
                                                        <i className="pi pi-sliders-h"></i>
                                                        تخصيص الألوان والخطوط
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* which themes: tabs with their counts, a line of colour under the one chosen */}
                                        <div className="sl-filters" role="tablist" aria-label="الثيمات">
                                            {FILTERS.map(x => (
                                                <button type="button" key={x.key} role="tab" aria-selected={filter === x.key}
                                                    className={filter === x.key ? 'is-on' : ''} onClick={() => this.setState({ filter: x.key })}>
                                                    <i className={x.icon}></i>
                                                    {x.label}
                                                    <small>{catalog.themes.filter(x.test).length}</small>
                                                </button>
                                            ))}
                                        </div>
                                        {/* themes made for one subject: only the specialties the catalogue has, general ones first */}
                                        {(() => {
                                            const counts = {};
                                            catalog.themes.forEach(t => { const k = specialtyOf(t); if (k) counts[k] = (counts[k] || 0) + 1; });
                                            const keys = Object.keys(SPECIALTY_NAMES).filter(k => counts[k]);
                                            if (!keys.length) return null;
                                            return (
                                                <div className="sl-specialties" role="group" aria-label="التخصص">
                                                    {['all', ...keys].map(k => (
                                                        <button type="button" key={k} aria-pressed={this.state.specialty === k}
                                                            onClick={() => this.setState({ specialty: k })}>
                                                            {k === 'all' ? 'كل التخصصات' : SPECIALTY_NAMES[k]}
                                                            {k !== 'all' && <small>{counts[k]}</small>}
                                                        </button>
                                                    ))}
                                                </div>
                                            );
                                        })()}

                                        {shown.length
                                            ? <div className="sl-grid" key={filter}>{shown.map(this.themeCard)}</div>
                                            : <div className="sl-empty"><i className="pi pi-images"></i>مفيش ثيمات هنا</div>}
                                    </Card>
                                </div>

                            </>
                        )}
                    </div>
                </div>

                {/* colours and fonts: a workbench beside the gallery rather than a second card under it. The panel is
                    portalled out of the page, so its insides carry .site-look to keep the page's styles. */}
                <Panel
                    wide
                    visible={!!(this.state.editOpen && theme && d)}
                    onHide={this.closeEditor}
                    title={theme ? `ألوان وخطوط ثيم «${theme.nameAr}»` : ''}
                    subtitle="جرّب براحتك — مفيش حاجة بتتحفظ غير لما تضغط حفظ"
                    actions={
                        <button type="button" className="ui-btn is-gold" disabled={saving || !changed || ratio < 3} onClick={this.save}>
                            <i className={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}></i>
                            {saving ? 'جاري الحفظ...' : 'حفظ وتطبيق'}
                        </button>
                    }
                    footer={catalog && (
                        <div className="site-look sl-panel-foot">
                            <span className={`sl-save-state ${changed ? 'is-dirty' : ''}`}>
                                <i className={changed ? 'pi pi-circle-on' : 'pi pi-check-circle'}></i>
                                {changed ? 'عندك تغييرات لسه ما اتحفظتش' : 'كل حاجة محفوظة'}
                            </span>
                            <div className="sl-save-actions">
                                {changed && (
                                    <button type="button" className="ui-btn is-small is-ghost" onClick={() => this.takeSelection(catalog.selection)}>
                                        <i className="pi pi-undo"></i>
                                        رجوع للمحفوظ
                                    </button>
                                )}
                                {site
                                    ? (
                                        <a className="ui-btn is-small is-secondary sl-preview-btn" href={this.previewUrl(this.state.theme, true)}
                                            target="_blank" rel="noopener noreferrer">
                                            <i className="pi pi-external-link"></i>
                                            معاينة بالألوان دي على موقعي
                                        </a>
                                    )
                                    : <small className="sl-note"><i className="pi pi-info-circle"></i> المعاينة على موقعك بتشتغل بعد ما تختار رابط موقعك.</small>}
                            </div>
                        </div>
                    )}
                >
                    {theme && d && (
                        <div className="site-look sl-in-panel">
                            <div className="sl-editor">
                                <div className="sl-controls">
                                    {this.step(1, 'ألوان جاهزة', 'مجموعات متناسقة مع الثيم — دوسة واحدة', (
                                        <div className="sl-presets">
                                            {(d.presets || []).map((p, i) => {
                                                const on = c.primary === p.primary && c.accent === p.accent && c.bg === p.background;
                                                return (
                                                    <button type="button" key={i} className={`sl-preset ${on ? 'on' : ''}`} title={p.name}
                                                        onClick={() => this.setState(i === 0
                                                            ? { primaryColor: '', accentColor: '', bgColor: '' }
                                                            : { primaryColor: p.primary, accentColor: p.accent, bgColor: p.background })}>
                                                        <span><i style={{ background: p.primary }}></i><i style={{ background: p.accent }}></i><i style={{ background: p.background }}></i></span>
                                                        {p.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ))}

                                    {this.step(2, 'أو اختار ألوانك', 'اللون الأساسي للأزرار، والتمييز للتفاصيل الصغيرة', (
                                        <>
                                            <div className="sl-colors">
                                                {this.colorInput('primaryColor', 'الأساسي (الأزرار)', c.primary)}
                                                {this.colorInput('accentColor', 'التمييز', c.accent)}
                                                {this.colorInput('bgColor', 'الخلفية', c.bg)}
                                            </div>
                                            {ratio < 3 && (
                                                <div className="sl-warn"><i className="pi pi-exclamation-triangle"></i>اللون الأساسي مش واضح على الخلفية (التباين {ratio.toFixed(1)}:1 — لازم 3:1 على الأقل). اختار لون أغمق أو خلفية أفتح.</div>
                                            )}
                                        </>
                                    ))}

                                    {this.step(3, 'الخطوط', 'خط للنص وخط للعناوين', (
                                        <div className="sl-fonts">
                                            <label>
                                                <span>خط النص</span>
                                                <select value={this.state.fontBody} onChange={(e) => this.setState({ fontBody: e.target.value })}>
                                                    <option value="">الافتراضي ({d.fonts.body})</option>
                                                    {catalog.arabicFonts.filter(n => n !== 'Lalezar').map(n => <option key={n} value={n}>{n}</option>)}
                                                    {!rtl && catalog.englishFonts.map(n => <option key={n} value={n}>{n}</option>)}
                                                </select>
                                            </label>
                                            <label>
                                                <span>خط العناوين</span>
                                                <select value={this.state.fontHeading} onChange={(e) => this.setState({ fontHeading: e.target.value })}>
                                                    <option value="">الافتراضي ({d.fonts.heading})</option>
                                                    <optgroup label="عربي">{catalog.arabicFonts.map(n => <option key={n} value={n}>{n}</option>)}</optgroup>
                                                    <optgroup label="English">{catalog.englishFonts.map(n => <option key={n} value={n}>{n}</option>)}</optgroup>
                                                </select>
                                            </label>
                                        </div>
                                    ))}
                                </div>

                                <div className="sl-stage">
                                    <div className="sl-stage-bar">
                                        <span className="sl-live"><i></i>معاينة مباشرة</span>
                                        {site && (
                                            <Segmented value={this.state.stage} onChange={(v) => this.setState({ stage: v })}
                                                items={[{ key: 'site', label: 'موقعي' }, { key: 'sample', label: 'عيّنة' }]} />
                                        )}
                                        <Segmented value={device} onChange={(v) => this.setState({ device: v })}
                                            items={[{ key: 'desktop', label: <i className="pi pi-desktop" title="كمبيوتر"></i> }, { key: 'mobile', label: <i className="pi pi-mobile" title="موبايل"></i> }]} />
                                    </div>
                                    {site && this.state.stage === 'site'
                                        ? this.browser(
                                            <SiteFrame
                                                src={this.previewUrl(this.state.theme, false)}
                                                device={device}
                                                choice={{ primaryColor: c.primary, accentColor: c.accent, bgColor: c.bg, fontBody: f.body, fontHeading: f.head }}
                                                draftKey={[c.primary, c.accent, c.bg, f.body, f.head].join('|')}
                                            />,
                                            `is-${device} is-site`
                                        )
                                        : this.browser(this.sample(d, c, f), `is-${device}`)}
                                </div>
                            </div>
                        </div>
                    )}
                </Panel>

                {this.linkWindow()}
                {this.applyWindow(theme, d, c, changed, ratio)}
                {this.previewWindow()}

                <Confirm
                    visible={!!this.state.confirmBuy}
                    onHide={() => !this.state.buying && this.setState({ confirmBuy: null })}
                    tone="warn"
                    icon="pi pi-shopping-cart"
                    title={this.state.confirmBuy ? `تشتري ثيم «${this.state.confirmBuy.nameAr}»؟` : ''}
                    note={this.state.confirmBuy
                        ? `هيتخصم ${usd(this.state.confirmBuy.priceUsd)} (${egp(this.state.confirmBuy.priceEgp)}) من محفظتك مرة واحدة، والثيم "${this.state.confirmBuy.nameAr}" يبقى بتاعك على طول.`
                        : ''}
                    confirmLabel="أيوه، اشتري"
                    busy={!!this.state.buying}
                    busyLabel="جاري الشراء..."
                    onConfirm={this.confirmBuyNow}
                />
            </>
        );
    }
}

export default SiteLook;
